import type { Component } from 'vue'
import type { FormField } from '@/ts/types/schema.types'
import ATextFieldMasked from '@/components/atoms/form-inputs/text/A-TextFieldMasked.vue'
import ASelectMasked from '@/components/atoms/form-inputs/select/A-SelectMasked.vue'
import AAutocomplete from '@/components/atoms/form-inputs/autocomplete/A-Autocomplete.vue'
import MContent from '@/components/molecules/M-Content.vue'
import MSlider from '@/components/molecules/form-inputs/slider/M-Slider.vue'
import MSegmentedControl from '@/components/molecules/form-inputs/m-segmented-control/M-SegmentedControl.vue'
import AToggleExtendedSettings from '@/components/atoms/form-inputs/toggle/A-ToggleExtendedSettings.vue'

const BREAKPOINTS = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const
type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

interface Components {
  [key: string]: Component
}

interface ColConfig {
  xs?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
  xxl?: number
}

export interface DynamicFormService {
  getComponents(): Components
  getResponsiveClasses(cols: ColConfig | undefined): string
  getFieldProps(field: FormField): Record<string, any>
}

export const DynamicFormService: DynamicFormService = {
  getComponents(): Components {
    return {
      ATextFieldMasked,
      ASelectMasked,
      AAutocomplete,
      AToggleExtendedSettings,
      MContent,
      MSlider,
      MSegmentedControl
    }
  },

  getResponsiveClasses(cols: ColConfig | undefined): string {
    if (!cols) return 'lnx-col-12'

    const classes: string[] = []

    for (const bp of BREAKPOINTS) {
      const value = cols[bp as Breakpoint]
      if (value !== undefined) {
        classes.push(bp === 'xs' ? `lnx-col-${value}` : `lnx-col-${bp}-${value}`)
      }
    }

    return classes.join(' ') || 'lnx-col-12' // Default to full width if no classes
  },

  getFieldProps(field: FormField): Record<string, any> {
    return {
      element: field,
      class: field.config.class,
      stepId: field.config.step_id,
      fieldId: field.config.field_id,
      type: field.config.type,
      inputMode: field.config.input_mode,
      alias: field.config.alias,
      placeholder: field.config.placeholder,
      text: field.config.text,
      disabled: field.config.disabled,
      clearable: field.config.clearable,
      min: field.config.min,
      max: field.config.max,
      stepWidth: field.config.step_width,
      modelValue: field.config.value,
      items: field.config.items,
      color: field.config.color,
      mask: field.config.mask,
      maskOptions: field.config.mask_options,
      maskedValue: field.config.masked_value,
      prependIcon: field.config.prepend_icon,
      appendIcon: field.config.append_icon,
      modalContent: field.config.modal_content,
      prefixValue: field.config.prefix,
      suffixValue: field.config.suffix,
      infoMessage: field.config.info_message,
      chipTag: field.config.chip_tag,
      loading: field.config.loading,
      block: field.config.block,
      readOnly: field.config.read_only,
      ariaLabel: field.config.aria_label,
      fullWidth: field.config.full_width,
      nullable: field.config.nullable ?? false
    }
  }
}
